import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiActivity, FiCalendar, FiPieChart, FiMessageSquare, FiTrendingUp,
  FiPlus, FiChevronLeft, FiChevronRight, FiTarget,
  FiX, FiStar, FiBarChart2, FiUser, FiAward, FiSettings, FiLogOut
} from 'react-icons/fi';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, getDoc, doc, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import ClientNutritionDashboard from './ClientNutritionDashboard';
import CoachLinkCard from './CoachLinkCard';
import ClientCalendar from './ClientCalendar';
import './ClientDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const ClientDashboard = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();

  // Data State
  const [clientProfile, setClientProfile] = useState(null);
  const [workoutHistory, setWorkoutHistory] = useState([]);
  const [mealHistory, setMealHistory] = useState([]);
  const [progressHistory, setProgressHistory] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [hasTrainer, setHasTrainer] = useState(false);
  const [trainerProfile, setTrainerProfile] = useState(null);
  const [macroCalculation, setMacroCalculation] = useState(null);
  const [assignedPrograms, setAssignedPrograms] = useState([]);
  const [activeProgram, setActiveProgram] = useState(null);
  const [programDetails, setProgramDetails] = useState(null);

  // UI State
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeWidget, setActiveWidget] = useState('overview');
  const [showMealLogger, setShowMealLogger] = useState(false);
  const [showWorkoutLogger, setShowWorkoutLogger] = useState(false);
  const [expandedCycles, setExpandedCycles] = useState({});
  const [expandedSessions, setExpandedSessions] = useState({});

  // Meal logging state
  const [newMeal, setNewMeal] = useState({
    name: '',
    foods: []
  });

  const [newFood, setNewFood] = useState({
    name: '',
    quantity: '',
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0
  });

  // Workout logging state
  const [newWorkout, setNewWorkout] = useState({
    program: '',
    exercises: [],
    duration: 0,
    notes: ''
  });

  const [newExercise, setNewExercise] = useState({
    name: '',
    sets: 3,
    targetReps: 10,
    completedSets: [],
    weight: 0
  });

  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(5);

  // Fetch client data from Firebase
  useEffect(() => {
    if (currentUser) {
      fetchClientData();
    }
  }, [currentUser]);

  const fetchClientData = async () => {
    try {
      setLoading(true);

      let trainerId = null;

      // Fetch client profile
      const profileDoc = await getDoc(doc(db, 'clients', currentUser.uid));
      if (profileDoc.exists()) {
        const profileData = { id: profileDoc.id, ...profileDoc.data() };
        setClientProfile(profileData);

        // Check if client has a trainer linked
        if (profileData.trainerId) {
          setHasTrainer(true);
          trainerId = profileData.trainerId;
        }
      }

      // Also check user document for trainerId
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists() && userDoc.data().trainerId) {
        setHasTrainer(true);
        trainerId = userDoc.data().trainerId;
      }

      // Fetch trainer profile if trainerId exists
      if (trainerId) {
        try {
          const trainerDoc = await getDoc(doc(db, 'users', trainerId));
          if (trainerDoc.exists()) {
            setTrainerProfile({ id: trainerDoc.id, ...trainerDoc.data() });
          }
        } catch (error) {
          console.error('Error fetching trainer profile:', error);
        }
      }

      // Fetch workout history
      const workoutsQuery = query(
        collection(db, 'workoutLogs'),
        where('userId', '==', currentUser.uid),
        orderBy('date', 'desc'),
        limit(10)
      );
      const workoutsSnapshot = await getDocs(workoutsQuery);
      setWorkoutHistory(workoutsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Fetch meal history
      const mealsQuery = query(
        collection(db, 'mealLogs'),
        where('userId', '==', currentUser.uid),
        orderBy('timestamp', 'desc'),
        limit(10)
      );
      const mealsSnapshot = await getDocs(mealsQuery);
      setMealHistory(mealsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Fetch progress entries
      const progressQuery = query(
        collection(db, 'progressEntries'),
        where('userId', '==', currentUser.uid),
        orderBy('date', 'desc'),
        limit(30)
      );
      const progressSnapshot = await getDocs(progressQuery);
      setProgressHistory(progressSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Fetch upcoming sessions
      const sessionsQuery = query(
        collection(db, 'sessions'),
        where('clientId', '==', currentUser.uid),
        where('status', '==', 'scheduled'),
        orderBy('date', 'asc'),
        limit(5)
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);
      setUpcomingSessions(sessionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Fetch macro calculation
      try {
        const macroDoc = await getDoc(doc(db, 'macroCalculations', currentUser.uid));
        if (macroDoc.exists()) {
          setMacroCalculation(macroDoc.data());
        }
      } catch (error) {
        console.error('Error fetching macro calculation:', error);
      }

      // Fetch assigned programs from subcollection
      try {
        const assignedProgramsRef = collection(db, 'users', currentUser.uid, 'assignedPrograms');
        const assignedProgramsQuery = query(
          assignedProgramsRef,
          where('status', '==', 'active'),
          orderBy('assignedAt', 'desc')
        );
        const assignedProgramsSnap = await getDocs(assignedProgramsQuery);

        const programs = [];
        for (const programDoc of assignedProgramsSnap.docs) {
          const assignmentData = { id: programDoc.id, ...programDoc.data() };
          programs.push(assignmentData);

          // If this is the first active program, fetch its full details
          if (!activeProgram) {
            try {
              const programDetailsDoc = await getDoc(doc(db, 'programs', assignmentData.programId));
              if (programDetailsDoc.exists()) {
                setActiveProgram(assignmentData);
                setProgramDetails({ id: programDetailsDoc.id, ...programDetailsDoc.data() });
              }
            } catch (error) {
              console.error('[ClientDashboard] Error fetching program details:', error);
            }
          }
        }

        setAssignedPrograms(programs);
        console.log('[ClientDashboard] Fetched assigned programs:', programs.length);
      } catch (error) {
        console.error('[ClientDashboard] Error fetching assigned programs:', error);
      }

    } catch (error) {
      console.error('Error fetching client data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle when client successfully links to a coach
  const handleCoachLinked = (trainerName) => {
    console.log(`[ClientDashboard] Successfully linked to coach: ${trainerName}`);
    setHasTrainer(true);
    // Refresh all client data to show updated information
    fetchClientData();
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: FiBarChart2 },
    { id: 'program', label: 'My Program', icon: FiActivity },
    { id: 'calendar', label: 'Schedule', icon: FiCalendar },
    { id: 'nutrition', label: 'Nutrition', icon: FiPieChart },
    { id: 'progress', label: 'Progress', icon: FiTrendingUp },
    { id: 'feedback', label: 'Feedback', icon: FiMessageSquare }
  ];

  const addFoodToMeal = () => {
    if (newFood.name && newFood.quantity) {
      setNewMeal(prev => ({
        ...prev,
        foods: [...prev.foods, { ...newFood, id: Date.now() }]
      }));
      setNewFood({ name: '', quantity: '', calories: 0, protein: 0, carbs: 0, fat: 0 });
    }
  };

  const addSetToExercise = () => {
    setNewExercise(prev => ({
      ...prev,
      completedSets: [...prev.completedSets, prev.targetReps]
    }));
  };

  const addExerciseToWorkout = () => {
    if (newExercise.name && newExercise.completedSets.length > 0) {
      setNewWorkout(prev => ({
        ...prev,
        exercises: [...prev.exercises, { ...newExercise, id: Date.now() }]
      }));
      setNewExercise({ name: '', sets: 3, targetReps: 10, completedSets: [], weight: 0 });
    }
  };

  const toggleCycle = (cycleIndex) => {
    setExpandedCycles(prev => ({
      ...prev,
      [cycleIndex]: !prev[cycleIndex]
    }));
  };

  const toggleSession = (cycleIndex, sessionIndex) => {
    const key = `${cycleIndex}-${sessionIndex}`;
    setExpandedSessions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Chart data - prepare from Firebase data
  const weightProgressData = progressHistory.length > 0 ? {
    labels: progressHistory.map(entry => {
      const date = entry.date?.toDate ? entry.date.toDate() : new Date(entry.date);
      return date.toLocaleDateString();
    }).reverse(),
    datasets: [
      {
        label: 'Weight (lbs)',
        data: progressHistory.map(entry => entry.weight || 0).reverse(),
        borderColor: '#ff6b35',
        backgroundColor: 'rgba(255, 107, 53, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  } : {
    labels: [],
    datasets: [{
      label: 'Weight (lbs)',
      data: [],
      borderColor: '#ff6b35',
      backgroundColor: 'rgba(255, 107, 53, 0.1)',
    }]
  };

  const bodyFatData = progressHistory.length > 0 ? {
    labels: progressHistory.map(entry => {
      const date = entry.date?.toDate ? entry.date.toDate() : new Date(entry.date);
      return date.toLocaleDateString();
    }).reverse(),
    datasets: [
      {
        label: 'Body Fat %',
        data: progressHistory.map(entry => entry.bodyFat || 0).reverse(),
        backgroundColor: ['#ff6b35', '#f4a261', '#4ecdc4'],
        borderColor: ['#ff6b35', '#f4a261', '#4ecdc4'],
        borderWidth: 2
      }
    ]
  } : {
    labels: [],
    datasets: [{
      label: 'Body Fat %',
      data: [],
      backgroundColor: ['#ff6b35', '#f4a261', '#4ecdc4'],
    }]
  };

  const macroDistribution = clientProfile?.nutritionPlan?.macros ? {
    labels: ['Protein', 'Carbs', 'Fat'],
    datasets: [
      {
        data: [
          (clientProfile.nutritionPlan.macros.protein * 4),
          (clientProfile.nutritionPlan.macros.carbs * 4),
          (clientProfile.nutritionPlan.macros.fat * 9)
        ],
        backgroundColor: ['#ff6b35', '#4ecdc4', '#45b7d1'],
        borderWidth: 0
      }
    ]
  } : {
    labels: ['Protein', 'Carbs', 'Fat'],
    datasets: [{
      data: [120, 160, 60],
      backgroundColor: ['#ff6b35', '#4ecdc4', '#45b7d1'],
      borderWidth: 0
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: 'white'
        }
      }
    },
    scales: {
      x: {
        ticks: { color: 'white' },
        grid: { color: 'rgba(255, 255, 255, 0.1)' }
      },
      y: {
        ticks: { color: 'white' },
        grid: { color: 'rgba(255, 255, 255, 0.1)' }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: 'white' }
      }
    }
  };

  const renderOverview = () => {
    // Calculate current stats from latest progress entry
    const latestProgress = progressHistory[0];
    const currentWeight = latestProgress?.weight || macroCalculation?.formData?.weight || 0;
    const currentHeight = macroCalculation?.formData?.height || 0;
    const goalWeight = clientProfile?.goals?.targetWeight || 'Not set';

    // Get user's first name
    const firstName = userProfile?.displayName?.split(' ')[0] || 'there';

    // Calculate streaks
    const trainingStreak = 0; // TODO: Calculate from workoutHistory
    const nutritionStreak = 0; // TODO: Calculate from mealHistory

    return (
    <div className="overview-grid-simple">
      {/* Welcome Message */}
      <div className="welcome-message">
        <h2>Welcome back, {firstName}</h2>
      </div>

      {/* Show CoachLinkCard if client doesn't have a trainer */}
      {!hasTrainer && (
        <div className="coach-link-section-banner">
          <CoachLinkCard onLinked={handleCoachLinked} />
        </div>
      )}

      {/* Top Left: Current Stats */}
      <div className="overview-container current-stats">
        <h3 className="orange-header">Current Stats</h3>
        <div className="stats-pill-grid">
          <div className="stat-pill">
            <span className="pill-label">Weight</span>
            <span className="pill-value">{currentWeight} kg</span>
          </div>
          <div className="stat-pill">
            <span className="pill-label">Height</span>
            <span className="pill-value">{currentHeight} cm</span>
          </div>
          <div className="stat-pill">
            <span className="pill-label">Program name</span>
            <span className="pill-value">{clientProfile?.program || 'None'}</span>
          </div>
          <div className="stat-pill">
            <span className="pill-label">Nutrition Goal</span>
            <span className="pill-value">{macroCalculation?.formData?.goal || 'Not set'}</span>
          </div>
          <div className="stat-pill">
            <span className="pill-label">Training Streak</span>
            <span className="pill-value">{trainingStreak} days</span>
          </div>
          <div className="stat-pill">
            <span className="pill-label">Nutrition Streak</span>
            <span className="pill-value">{nutritionStreak} days</span>
          </div>
          <div className="stat-pill">
            <span className="pill-label">Target Weight</span>
            <span className="pill-value">{goalWeight} kg</span>
          </div>
          <div className="stat-pill">
            <span className="pill-label">Target bf %</span>
            <span className="pill-value">{clientProfile?.goals?.targetBodyFat || '--'}%</span>
          </div>
        </div>
      </div>

      {/* Top Right: Daily Target */}
      <div className="overview-container daily-target">
        <h3 className="orange-header">Daily Target</h3>
        {macroCalculation ? (
          <div className="daily-target-content">
            <div className="macros-section">
              <h4>Macros</h4>
              <div className="macros-display-inline">
                <div className="macro-inline-item">
                  <span className="macro-inline-label">Calories</span>
                  <span className="macro-inline-value">{macroCalculation.results?.targetCalories || 0}</span>
                </div>
                <div className="macro-inline-item">
                  <span className="macro-inline-label">Protein</span>
                  <span className="macro-inline-value">{macroCalculation.results?.macros?.protein || 0}g</span>
                </div>
                <div className="macro-inline-item">
                  <span className="macro-inline-label">Carbs</span>
                  <span className="macro-inline-value">{macroCalculation.results?.macros?.carbs || 0}g</span>
                </div>
                <div className="macro-inline-item">
                  <span className="macro-inline-label">Fat</span>
                  <span className="macro-inline-value">{macroCalculation.results?.macros?.fat || 0}g</span>
                </div>
              </div>
            </div>
            <div className="target-additional">
              <div className="stat-pill">
                <span className="pill-label">No of training sessions</span>
                <span className="pill-value">{upcomingSessions.length}</span>
              </div>
              <div className="stat-pill">
                <span className="pill-label">Stretching etc</span>
                <span className="pill-value">--</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <FiTarget size={32} />
            <p>Set your macro targets in Nutrition Tools</p>
          </div>
        )}
      </div>

      {/* Bottom Left: Today's Training Plan */}
      <div className="overview-container training-plan">
        <h3 className="orange-header">Today's Training Plan</h3>
        {upcomingSessions.length > 0 || workoutHistory.length > 0 ? (
          <div className="training-plan-content">
            <div className="training-session">
              <div className="session-time">
                <FiCalendar />
                <span>{upcomingSessions[0]?.date?.toDate ? upcomingSessions[0].date.toDate().toLocaleDateString() : 'Today'}</span>
              </div>
              <h4>{upcomingSessions[0]?.type || 'Personal Training Session'}</h4>
              <p className="session-focus">Focus: {upcomingSessions[0]?.focus || clientProfile?.program || 'Full Body Workout'}</p>
            </div>
          </div>
        ) : (
          <div className="empty-state-simple">
            <FiActivity size={32} />
            <p>No training plan for today</p>
          </div>
        )}
      </div>

      {/* Bottom Right: Today's Menu */}
      <div className="overview-container todays-menu">
        <h3 className="orange-header">Today's Menu</h3>
        {mealHistory.length > 0 ? (
          <div className="menu-content">
            <div className="meals-list">
              {mealHistory.slice(0, 5).map((meal, index) => (
                <div key={index} className="meal-item-simple">
                  <div className="meal-info">
                    <span className="meal-type">{meal.mealType || `Meal ${index + 1}`}</span>
                    <span className="meal-calories">{meal.totalCalories || 0} kcal</span>
                  </div>
                  <div className="meal-macros-simple">
                    <span>P: {meal.totalProtein || 0}g</span>
                    <span>C: {meal.totalCarbs || 0}g</span>
                    <span>F: {meal.totalFat || 0}g</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="empty-state-simple">
            <FiPieChart size={32} />
            <p>No meals logged today</p>
          </div>
        )}
      </div>
    </div>
    );
  };

  const renderProgram = () => {
    if (!activeProgram || !programDetails) {
      return (
        <div className="program-section">
          <div className="program-header">
            <h2>My Training Program</h2>
            <span className="program-name">No Program Assigned</span>
          </div>
          <div className="empty-state">
            <FiActivity size={48} />
            <p>You don't have an active training program yet.</p>
            <p>Your coach will assign you a program soon!</p>
          </div>
        </div>
      );
    }

    return (
      <div className="program-section">
        <div className="program-header">
          <h2>My Training Program</h2>
          <span className="program-name">{activeProgram.programName}</span>
        </div>

        <div className="program-info-cards">
          <div className="info-card">
            <span className="info-label">Coach</span>
            <span className="info-value">{activeProgram.coachName}</span>
          </div>
          <div className="info-card">
            <span className="info-label">Start Date</span>
            <span className="info-value">{new Date(activeProgram.startDate).toLocaleDateString()}</span>
          </div>
          <div className="info-card">
            <span className="info-label">Cycles</span>
            <span className="info-value">{activeProgram.numberOfCycles} cycles</span>
          </div>
          <div className="info-card">
            <span className="info-label">Sessions/Cycle</span>
            <span className="info-value">{activeProgram.sessionsPerCycle} sessions</span>
          </div>
        </div>

        {programDetails.cycles && programDetails.cycles.length > 0 && (
          <div className="current-workout">
            <h3>Program Overview</h3>
            {programDetails.cycles.map((cycle, cycleIndex) => {
              const isCycleExpanded = expandedCycles[cycleIndex];
              return (
                <div key={cycleIndex} className="cycle-card">
                  <div className="cycle-header" onClick={() => toggleCycle(cycleIndex)} style={{ cursor: 'pointer' }}>
                    <h4>
                      {isCycleExpanded ? <FiChevronRight style={{ transform: 'rotate(90deg)', transition: 'transform 0.3s' }} /> : <FiChevronRight />}
                      Cycle {cycle.cycleNumber}
                    </h4>
                    <span className="cycle-sessions">{cycle.sessions?.length || 0} sessions</span>
                  </div>

                  {isCycleExpanded && cycle.sessions && cycle.sessions.length > 0 && (
                    <div className="sessions-list">
                      {cycle.sessions.map((session, sessionIndex) => {
                        const sessionKey = `${cycleIndex}-${sessionIndex}`;
                        const isSessionExpanded = expandedSessions[sessionKey];
                        return (
                          <div key={sessionIndex}>
                            <div
                              className="session-item"
                              onClick={() => toggleSession(cycleIndex, sessionIndex)}
                              style={{ cursor: 'pointer' }}
                            >
                              <span className="session-name">
                                {isSessionExpanded ? <FiChevronRight style={{ transform: 'rotate(90deg)', transition: 'transform 0.3s', marginRight: '0.5rem' }} /> : <FiChevronRight style={{ marginRight: '0.5rem' }} />}
                                {session.name || `Session ${sessionIndex + 1}`}
                              </span>
                              <span className="session-blocks">{session.blocks?.length || 0} blocks</span>
                            </div>

                            {isSessionExpanded && session.blocks && session.blocks.length > 0 && (
                              <div className="blocks-container">
                                {session.blocks.map((block, blockIndex) => (
                                  <div key={blockIndex} className="block-card">
                                    <div className="block-header">
                                      <h5>{block.blockName || `Block ${blockIndex + 1}`}</h5>
                                      <span className="block-meta">
                                        {block.workoutStyle === 'superset' && <span className="workout-style">Superset</span>}
                                        {block.workoutStyle === 'circuit' && <span className="workout-style">Circuit</span>}
                                        {block.circuitRounds > 1 && <span className="circuit-rounds">{block.circuitRounds} rounds</span>}
                                      </span>
                                    </div>
                                    <div className="exercises-container">
                                      {block.exercises && block.exercises.map((exercise, exIndex) => (
                                        <div key={exIndex} className="exercise-detail">
                                          <div className="exercise-number">{exIndex + 1}</div>
                                          <div className="exercise-content">
                                            <div className="exercise-title">{exercise.exerciseName}</div>
                                            <div className="exercise-specs">
                                              {exercise.primaryMetrics?.sets && (
                                                <span className="spec-item">{exercise.primaryMetrics.sets} sets</span>
                                              )}
                                              {exercise.primaryMetrics?.reps && (
                                                <span className="spec-item">{exercise.primaryMetrics.reps} reps</span>
                                              )}
                                              {exercise.primaryMetrics?.rest && (
                                                <span className="spec-item">{exercise.primaryMetrics.rest}s rest</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      <div className="workout-history">
        <h3>Recent Workouts</h3>
        {workoutHistory.length > 0 ? (
          workoutHistory.map((workout, index) => (
            <div key={index} className="history-card">
              <div className="history-header">
                <span className="history-date">
                  {workout.date?.toDate ? workout.date.toDate().toLocaleDateString() : 'Recent'}
                </span>
                <span className="history-duration">{workout.duration || 0} min</span>
              </div>
              <div className="history-program">{workout.program || clientProfile?.program || 'Training'}</div>
              <div className="history-exercises">
                {workout.exercises?.slice(0, 2).map((exercise, i) => (
                  <span key={i} className="exercise-summary">
                    {exercise.name}: {exercise.completedSets?.join(', ') || 0} reps
                  </span>
                ))}
              </div>
              {workout.notes && <p className="history-notes">{workout.notes}</p>}
            </div>
          ))
        ) : (
          <div className="empty-state">
            <FiActivity size={32} />
            <p>No workouts logged yet</p>
            <button onClick={() => setShowWorkoutLogger(true)}>
              <FiPlus /> Log First Workout
            </button>
          </div>
        )}
      </div>

      {showWorkoutLogger && (
        <div className="workout-logger-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Log Workout</h3>
              <button onClick={() => setShowWorkoutLogger(false)}>
                <FiX />
              </button>
            </div>
            
            <div className="logger-form">
              <div className="exercise-logger">
                <h4>Add Exercise</h4>
                <input
                  type="text"
                  placeholder="Exercise Name"
                  value={newExercise.name}
                  onChange={(e) => setNewExercise(prev => ({ ...prev, name: e.target.value }))}
                />
                <div className="sets-logger">
                  <input
                    type="number"
                    placeholder="Weight (lbs)"
                    value={newExercise.weight}
                    onChange={(e) => setNewExercise(prev => ({ ...prev, weight: parseInt(e.target.value) }))}
                  />
                  <input
                    type="number"
                    placeholder="Target Reps"
                    value={newExercise.targetReps}
                    onChange={(e) => setNewExercise(prev => ({ ...prev, targetReps: parseInt(e.target.value) }))}
                  />
                  <button onClick={addSetToExercise}>Add Set</button>
                </div>
                
                {newExercise.completedSets.length > 0 && (
                  <div className="completed-sets">
                    <p>Sets: {newExercise.completedSets.join(', ')} reps</p>
                  </div>
                )}
                
                <button onClick={addExerciseToWorkout} className="add-exercise">
                  Add Exercise
                </button>
              </div>
              
              <textarea
                placeholder="Workout notes..."
                value={newWorkout.notes}
                onChange={(e) => setNewWorkout(prev => ({ ...prev, notes: e.target.value }))}
              />
              
              <button className="save-workout">Save Workout</button>
            </div>
          </div>
        </div>
      )}
    </div>
    );
  };

  const renderCalendar = () => <ClientCalendar />;

  const renderNutrition = () => (
    <ClientNutritionDashboard />
  );

  const renderProgress = () => {
    // Calculate progress metrics from real data
    const weightChange = progressHistory.length >= 2
      ? Math.abs(progressHistory[0]?.weight - progressHistory[progressHistory.length - 1]?.weight || 0).toFixed(1)
      : 0;

    const bodyFatChange = progressHistory.length >= 2
      ? Math.abs(progressHistory[0]?.bodyFat - progressHistory[progressHistory.length - 1]?.bodyFat || 0).toFixed(1)
      : 0;

    const compliance = Math.round((workoutHistory.length / 12) * 100) || 0;

    return (
    <div className="progress-section">
      <h2>My Progress</h2>

      <div className="progress-overview">
        <div className="progress-stat">
          <h3>Weight Change</h3>
          <div className="stat-large">{weightChange} lbs</div>
          <p>
            {progressHistory.length >= 2
              ? 'Since starting'
              : 'Start tracking to see progress'}
          </p>
        </div>

        <div className="progress-stat">
          <h3>Body Fat</h3>
          <div className="stat-large">{bodyFatChange}%</div>
          <p>
            {progressHistory.length >= 2
              ? 'Reduction achieved'
              : 'Track body fat to see changes'}
          </p>
        </div>

        <div className="progress-stat">
          <h3>Compliance</h3>
          <div className="stat-large">{compliance}%</div>
          <p>Program adherence</p>
        </div>
      </div>

      <div className="progress-charts">
        <div className="chart-card">
          <h3>Weight Progress</h3>
          <div className="chart-container">
            <Line data={weightProgressData} options={chartOptions} height={300} />
          </div>
        </div>
        
        <div className="chart-card">
          <h3>Body Fat Progress</h3>
          <div className="chart-container">
            <Bar data={bodyFatData} options={chartOptions} height={300} />
          </div>
        </div>
      </div>

      <div className="measurements">
        <h3>Body Measurements</h3>
        <div className="measurement-grid">
          <div className="measurement-item">
            <label>Waist</label>
            <span>30.5"</span>
            <small>-1.5" from start</small>
          </div>
          <div className="measurement-item">
            <label>Hips</label>
            <span>39"</span>
            <small>-1" from start</small>
          </div>
          <div className="measurement-item">
            <label>Chest</label>
            <span>35.5"</span>
            <small>-0.5" from start</small>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderFeedback = () => (
    <div className="feedback-section">
      <h2>Trainer Feedback</h2>
      
      <div className="send-feedback">
        <h3>Send Message to Trainer</h3>
        <textarea
          placeholder="How are you feeling? Any questions or concerns?"
          value={feedbackMessage}
          onChange={(e) => setFeedbackMessage(e.target.value)}
        />
        
        <div className="rating-section">
          <label>Rate your experience this week:</label>
          <div className="star-rating">
            {[1, 2, 3, 4, 5].map(star => (
              <FiStar
                key={star}
                className={star <= feedbackRating ? 'filled' : ''}
                onClick={() => setFeedbackRating(star)}
              />
            ))}
          </div>
        </div>
        
        <button className="send-btn">Send Feedback</button>
      </div>

      <div className="trainer-messages">
        <h3>Messages from Trainer</h3>
        <div className="message-card">
          <div className="message-header">
            <strong>Coach Johnson</strong>
            <span className="message-date">June 16, 2024</span>
          </div>
          <p>Great work on yesterday's session! I can see you're getting stronger. Keep focusing on your form and we'll increase the weights next week.</p>
        </div>
        
        <div className="message-card">
          <div className="message-header">
            <strong>Coach Johnson</strong>
            <span className="message-date">June 14, 2024</span>
          </div>
          <p>Remember to hit your protein targets this week. Your progress is excellent, and proper nutrition will help you reach your goals faster!</p>
        </div>
      </div>
    </div>
  );

  const renderActiveWidget = () => {
    switch(activeWidget) {
      case 'overview': return renderOverview();
      case 'program': return renderProgram();
      case 'calendar': return renderCalendar();
      case 'nutrition': return renderNutrition();
      case 'progress': return renderProgress();
      case 'feedback': return renderFeedback();
      default: return renderOverview();
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="client-dashboard">
        <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            {!sidebarCollapsed && (
              hasTrainer && trainerProfile ? (
                <div className="coach-info">
                  <div className="coach-avatar">
                    {trainerProfile.displayName?.[0]?.toUpperCase() || 'C'}
                  </div>
                  <div className="coach-details">
                    <span className="coach-label">Your Coach</span>
                    <h3 className="coach-name">{trainerProfile.displayName || trainerProfile.businessName || 'Coach'}</h3>
                  </div>
                </div>
              ) : (
                <h2>My Dashboard</h2>
              )
            )}
            <button
              className="collapse-btn"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              {sidebarCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
            </button>
          </div>

          <nav className="sidebar-nav">
            {sidebarItems.map(item => (
              <button
                key={item.id}
                className={`nav-item ${activeWidget === item.id ? 'active' : ''}`}
                onClick={() => setActiveWidget(item.id)}
              >
                <item.icon />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
            ))}
          </nav>

          {/* Quick Actions in Sidebar */}
          {!sidebarCollapsed && (
            <div className="sidebar-quick-actions">
              <h4>Quick Actions</h4>
              <div className="quick-actions-list">
                <button className="quick-action-item" onClick={() => setActiveWidget('nutrition')}>
                  <FiPieChart />
                  <span>View Meal Plan</span>
                </button>
                <button className="quick-action-item" onClick={() => setActiveWidget('program')}>
                  <FiActivity />
                  <span>Log Workout</span>
                </button>
                <button className="quick-action-item" onClick={() => setActiveWidget('feedback')}>
                  <FiMessageSquare />
                  <span>Message Coach</span>
                </button>
              </div>
            </div>
          )}

          {/* Settings at Bottom */}
          {!sidebarCollapsed && (
            <div className="sidebar-footer">
              <button className="footer-btn" onClick={() => navigate('/unique-physique/client-dashboard/settings')}>
                <FiSettings />
                <span>Settings</span>
              </button>
              <button className="footer-btn" onClick={handleLogout}>
                <FiLogOut />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>

        <div className="main-content">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="client-dashboard">
      <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          {!sidebarCollapsed && (
            hasTrainer && trainerProfile ? (
              <div className="coach-info">
                <div className="coach-avatar">
                  {trainerProfile.displayName?.[0]?.toUpperCase() || 'C'}
                </div>
                <div className="coach-details">
                  <span className="coach-label">Your Coach</span>
                  <h3 className="coach-name">{trainerProfile.displayName || trainerProfile.businessName || 'Coach'}</h3>
                </div>
              </div>
            ) : (
              <h2>My Dashboard</h2>
            )
          )}
          <button
            className="collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {sidebarItems.map(item => (
            <button
              key={item.id}
              className={`nav-item ${activeWidget === item.id ? 'active' : ''}`}
              onClick={() => setActiveWidget(item.id)}
            >
              <item.icon />
              {!sidebarCollapsed && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Quick Actions in Sidebar */}
        {!sidebarCollapsed && (
          <div className="sidebar-quick-actions">
            <h4>Quick Actions</h4>
            <div className="quick-actions-list">
              <button className="quick-action-item" onClick={() => setActiveWidget('nutrition')}>
                <FiPieChart />
                <span>View Meal Plan</span>
              </button>
              <button className="quick-action-item" onClick={() => setActiveWidget('program')}>
                <FiActivity />
                <span>Log Workout</span>
              </button>
              <button className="quick-action-item" onClick={() => setActiveWidget('feedback')}>
                <FiMessageSquare />
                <span>Message Coach</span>
              </button>
            </div>
          </div>
        )}

        {/* Settings at Bottom */}
        {!sidebarCollapsed && (
          <div className="sidebar-footer">
            <button className="footer-btn" onClick={() => navigate('/unique-physique/client-dashboard/settings')}>
              <FiSettings />
              <span>Settings</span>
            </button>
            <button className="footer-btn" onClick={handleLogout}>
              <FiLogOut />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>

      <div className="main-content">
        <div className="widget-container">
          {renderActiveWidget()}
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;